import { theme, useResolvedScheme } from "@elyxeer/ui";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  HashRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";

import { AnalyticsTracker } from "~/components/analytics/AnalyticsTracker";
import { AuthGate } from "~/components/auth/AuthGate";
import { MemberGate } from "~/components/membership/MemberGate";
import { OnboardingGate } from "~/components/onboarding/OnboardingGate";
import { RoutineReminderSync } from "~/components/routine/RoutineReminderSync";
import { AppShell } from "~/components/shell/AppShell";
import { I18nProvider } from "~/i18n";
import { ACCOUNT_DELETION_PATH } from "~/lib/account-deletion";
import { initializeNativeShell, syncStatusBarStyle } from "~/lib/capacitor";
import { inviteRouteFromUrl } from "~/lib/invite-links";
import { trackKeyboardInset } from "~/lib/keyboard-inset";
import { LEGAL_ROUTE_PATH } from "~/lib/legal";
import { LIVE_ROUTE_PATH } from "~/lib/live-links";
import type { NavigationKind } from "~/lib/navigation-depth";
import { canGoBack, recordNavigation } from "~/lib/navigation-depth";
import { WELCOME_PATH } from "~/lib/onboarding";
import { initializePushNotifications } from "~/lib/push-notifications";
import { queryClient, queryKeys } from "~/lib/query-client";
import { ROUTINE_PATH } from "~/lib/routine";
import { initializeRoutineReminders } from "~/lib/routine-reminders";
import { AccountDeletionRoute } from "~/routes/AccountDeletionRoute";
import { ArticleRoute } from "~/routes/ArticleRoute";
import { DashboardHighlightRoute } from "~/routes/DashboardHighlightRoute";
import { EventRoute } from "~/routes/EventRoute";
import { EventsRoute } from "~/routes/EventsRoute";
import { FeedRoute } from "~/routes/FeedRoute";
import { InviteRoute } from "~/routes/InviteRoute";
import { LegalRoute } from "~/routes/LegalRoute";
import { LiveEventRoute } from "~/routes/LiveEventRoute";
import { MembershipUpsellRoute } from "~/routes/MembershipRoute";
import { NotificationsCenterRoute } from "~/routes/NotificationsCenterRoute";
import { PaywallRoute } from "~/routes/PaywallRoute";
import { AppearanceRoute } from "~/routes/profile/AppearanceRoute";
import { EmailRoute } from "~/routes/profile/EmailRoute";
// English is held back until release — see MEMBER_LOCALES in ~/i18n/use-locale.
// import { LanguageRoute } from "~/routes/profile/LanguageRoute";
import { LegalInformationRoute } from "~/routes/profile/LegalInformationRoute";
import { NotificationsRoute } from "~/routes/profile/NotificationsRoute";
import { RemindersRoute } from "~/routes/profile/RemindersRoute";
import { UsernameRoute } from "~/routes/profile/UsernameRoute";
import { ProfileRoute } from "~/routes/ProfileRoute";
import { ProtocolsRoute } from "~/routes/ProtocolsRoute";
import { RoutineRoute } from "~/routes/RoutineRoute";
import { SAVED_PATH, SavedRoute } from "~/routes/SavedRoute";
import { WelcomeRoute } from "~/routes/WelcomeRoute";
import { useAuthStore } from "~/store/auth-store";

/**
 * Native chrome setup.
 *
 * Lives inside the providers because it needs the resolved colour scheme to pick a
 * readable status-bar style. A no-op on the web.
 */
function NativeShell() {
  const scheme = useResolvedScheme();
  const navigationType = useNavigationType();
  const location = useLocation();
  const navigate = useNavigate();

  // Every router transition is folded into the shared depth counter, which both the
  // Android hardware back button and the phone header's back chevron read.
  useEffect(() => {
    recordNavigation(navigationType as NavigationKind);
  }, [location, navigationType]);

  useEffect(() => {
    let removeListeners: (() => void) | undefined;
    let removePushListeners: (() => void) | undefined;
    let removeReminderListeners: (() => void) | undefined;
    let disposed = false;
    void initializeNativeShell(
      scheme,
      () => {
        if (canGoBack()) {
          window.history.back();
          return true;
        }
        // Nothing left to pop — Capacitor exits the app.
        return false;
      },
      (url) => {
        const route = inviteRouteFromUrl(url);
        if (route) navigate(route);
      },
    ).then((remove) => {
      if (disposed) remove();
      else removeListeners = remove;
    });
    void initializePushNotifications({
      // The route is derived from the payload inside `notification-routes.ts` and
      // parked until here, so a launch tap survives the auth store hydrating.
      onOpenRoute: (route) => navigate(route),
      onNotification: (kind) => {
        const userId = useAuthStore.getState().user?.id;
        if (!userId) return;
        void queryClient.invalidateQueries({
          queryKey:
            kind === "chat_message"
              ? queryKeys.chatThread(userId)
              : // The bell's badge and the notification centre are separate,
                // locale-keyed queries; invalidating the identity-bound root
                // refreshes both without this file owning their key shape.
                queryKeys.userScoped,
        });
      },
    }).then((remove) => {
      if (disposed) remove();
      else removePushListeners = remove;
    });
    // A reminder tap parks its route the same way a push tap does, so the drain the
    // push listeners installed above opens it — cold start included.
    void initializeRoutineReminders().then((remove) => {
      if (disposed) remove();
      else removeReminderListeners = remove;
    });
    return () => {
      disposed = true;
      removeListeners?.();
      removePushListeners?.();
      removeReminderListeners?.();
    };
    // Runs once: re-registering the listener on every scheme change would stack them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void syncStatusBarStyle(scheme);
  }, [scheme]);

  return null;
}

export function App() {
  // The keyboard's height, published as a CSS variable for the screens that dock
  // something to the bottom edge. Web as much as native, so it does not belong in
  // NativeShell — and it is a browser measurement, not a plugin call.
  useEffect(() => trackKeyboardInset(), []);

  return (
    <ThemeProvider theme={theme} defaultMode="system" disableTransitionOnChange>
      <CssBaseline enableColorScheme />
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          {/*
            Hash routing on purpose: the native build is served from the filesystem via
            `capacitor://`, where there is no server to rewrite deep paths back to
            index.html. Firebase Hosting is configured with the same single-page
            rewrite, so both targets behave identically.
          */}
          <Router>
            {/* Inside the Router: NativeShell tracks navigation depth for the
                Android hardware back button. */}
            <NativeShell />
            {/* Outside every gate: it has to see the account leave, and lose access,
                as well as arrive. On the web it has nothing to schedule. */}
            <RoutineReminderSync />
            {/* Also inside the Router: it records a screen view per navigation. */}
            <AnalyticsTracker />
            <AuthGate>
              <Routes>
                {/* The hard paywall lives outside the shell: no tab bar, no rail —
                    a non-member's whole app. It sends members back to /news. */}
                <Route path="/paywall" element={<PaywallRoute />} />
                {/* Stable public URL for Google Play's external deletion resource.
                    It sits outside the member gate so non-members can use it too. */}
                <Route
                  path={ACCOUNT_DELETION_PATH}
                  element={<AccountDeletionRoute />}
                />
                {/* Public invitations stay outside the member gate: a signed-in
                    non-member has to be able to claim one before gaining access. */}
                <Route path="/invite/:token" element={<InviteRoute />} />
                {/* Counsel's documents. Outside every gate, and registered again in
                    `AuthGate` for the signed-out half — a member must be able to read
                    what they are accepting before there is an account to accept it. */}
                <Route path={LEGAL_ROUTE_PATH} element={<LegalRoute />} />
                {/* The webinar registration page marketing shares. Outside every gate
                    — an outsider has no session — and registered again in `AuthGate`
                    for the signed-out half, like the legal documents. */}
                <Route path={LIVE_ROUTE_PATH} element={<LiveEventRoute />} />
                {/* Everything else — content and the member's profile — needs a
                    confirmed subscription or an administrator-granted app override.
                    Unsynced regular accounts stay on the paywall. */}
                <Route element={<MemberGate />}>
                  {/* Orientation for Protocol 30.5. Outside both the shell and the
                      onboarding gate: it is where the gate sends people, so it cannot
                      sit behind it, and on a first run the call to action is meant to
                      be the only way on. */}
                  <Route path={WELCOME_PATH} element={<WelcomeRoute />} />
                  <Route element={<OnboardingGate />}>
                    {/* Highlights are immersive readers rather than tabs: their own
                        back control returns to the dashboard, including from a deep link. */}
                    <Route
                      path="/highlights/:slug"
                      element={<DashboardHighlightRoute />}
                    />
                    <Route element={<AppShell />}>
                      <Route path="/news" element={<FeedRoute />} />
                      <Route path="/news/:slug" element={<ArticleRoute />} />
                      <Route path="/events" element={<EventsRoute />} />
                      <Route path="/events/:slug" element={<EventRoute />} />
                      <Route path="/protocols" element={<ProtocolsRoute />} />
                      <Route path={SAVED_PATH} element={<SavedRoute />} />
                      {/* Inside the shell, like the notification centre: the routine
                          is somewhere the member comes back to twice a day. */}
                      <Route path={ROUTINE_PATH} element={<RoutineRoute />} />
                      {/* Inside the shell: the reminder list is a place the member
                          comes back to, not a modal, so the tab bar stays put. */}
                      <Route
                        path="/notifications"
                        element={<NotificationsCenterRoute />}
                      />
                      <Route
                        path="/membership"
                        element={<MembershipUpsellRoute />}
                      />
                      <Route path="/profile" element={<ProfileRoute />} />
                      <Route
                        path="/profile/legal"
                        element={<LegalInformationRoute />}
                      />
                      <Route path="/profile/email" element={<EmailRoute />} />
                      <Route
                        path="/profile/username"
                        element={<UsernameRoute />}
                      />
                      <Route
                        path="/profile/notifications"
                        element={<NotificationsRoute />}
                      />
                      <Route
                        path="/profile/reminders"
                        element={<RemindersRoute />}
                      />
                      {/* Hidden until English is released.
                      <Route
                        path="/profile/language"
                        element={<LanguageRoute />}
                      />
                      */}
                      <Route
                        path="/profile/appearance"
                        element={<AppearanceRoute />}
                      />
                    </Route>
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/news" replace />} />
              </Routes>
            </AuthGate>
          </Router>
        </QueryClientProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
